import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Calendar, Search, X, CheckCircle, XCircle, User, Car, Bike, Trophy, DollarSign, RefreshCw, Download, Filter, PenTool as Tool, UserPlus, AlertCircle, RotateCcw, Clock, Truck } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { emailService } from '../../lib/emailService';
import Pagination from '../../components/Pagination';
import { formatCurrency } from '../../lib/currencyUtils';
import { exportToExcel } from '../../utils/excelExport';
import ReEntryManagement from '../../components/admin/ReEntryManagement';
import { formatDateForDisplay, isEventInPast } from '../../utils/dateUtils';

interface EventRegistration {
  id: string;
  event_id: string;
  category_id: string;
  user_id: string | null;
  temporary_user_id: string | null;
  driver_profile_id: string;
  amount_paid: number;
  status: string;
  payment_status: string;
  has_tool_car: boolean;
  tool_car_count: number;
  created_at: string;
  updated_at: string;
  full_name?: string;
  re_entry_fee?: number;
  allows_re_entry?: boolean;
  event: {
    name: string;
    event_date: string;
  };
  category: {
    name: string;
    vehicle_type: string;
    registration_fee: number;
    global_category_id?: string;
  };
  user?: {
    full_name: string;
    email: string;
    phone: string;
  };
  driver_profile?: {
    vehicle_type: string;
    vehicle_info: any;
    personal_info: any;
  };
  temporary_user?: {
    name: string;
    race_number: string;
  };
  original_registration_id?: string; // Added for re-entry fee
  re_entry_count?: number; // Added for re-entry count
}

export default function EventRegistrationManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('registrations');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('all');
  const [showPastEvents, setShowPastEvents] = useState(true); // Default to showing all events for registration management
  const [showToolCarOnly, setShowToolCarOnly] = useState(false);
  const [events, setEvents] = useState<any[]>([]);
  
  // Quick registration modal state
  const [showQuickRegModal, setShowQuickRegModal] = useState(false);
  const [quickRegEvent, setQuickRegEvent] = useState<any>(null);
  const [quickRegCategory, setQuickRegCategory] = useState<any>(null);
  const [quickRegName, setQuickRegName] = useState('');
  const [quickRegRaceNumber, setQuickRegRaceNumber] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [raceNumberExists, setRaceNumberExists] = useState(false);
  const [existingDriverName, setExistingDriverName] = useState('');
  const [isCreatingRegistration, setIsCreatingRegistration] = useState(false);
  const [availableEvents, setAvailableEvents] = useState<any[]>([]);
  const [availableCategories, setAvailableCategories] = useState<any[]>([]);
  const [quickRegUser, setQuickRegUser] = useState<any>(null); // Added for quick registration user
  const [revenueData, setRevenueData] = useState<any[]>([]);

  // Add state for admin re-entry registration
  const [isRegisteringReEntry, setIsRegisteringReEntry] = useState(false);
  const [showAdminReEntryModal, setShowAdminReEntryModal] = useState(false);
  const [adminReEntryRegistration, setAdminReEntryRegistration] = useState<EventRegistration | null>(null);

  // Debounce search term to prevent too many re-renders
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch all registrations once (no pagination)
  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('event_registrations')
        .select(`
          *,
          event:events!event_registrations_event_id_fkey(
            name,
            event_date
          ),
          category:event_categories(
            name,
            vehicle_type,
            registration_fee,
            global_category_id
          ),
          user:users(
            full_name,
            email,
            phone
          ),
          temporary_user:temporary_user!temporary_user_id(
            name,
            race_number
          ),
          driver_profile:driver_profiles(
            vehicle_type,
            vehicle_info,
            personal_info
          )
        `);

      // Apply filters
      if (!selectedEvent || selectedEvent === 'all') {
        // No event filter - show all events
      } else {
        query = query.eq('event_id', selectedEvent);
      }
      if (selectedStatus && selectedStatus !== 'all') query = query.eq('status', selectedStatus);
      if (selectedPaymentStatus && selectedPaymentStatus !== 'all') query = query.eq('payment_status', selectedPaymentStatus);
      if (showToolCarOnly) query = query.eq('category.vehicle_type', 'tool_car');

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching registrations:', error);
        console.error('Query details:', { selectedEvent, selectedStatus, selectedPaymentStatus, showToolCarOnly });
        return;
      }

      console.log('Raw registration data:', data);
      console.log('Number of registrations found:', data?.length || 0);

      // Map the data to include fees and re-entry information
      const registrationsWithFees = (data || []).map((registration: any) => {
        // Debug temporary user data
        if (registration.temporary_user_id && !registration.temporary_user) {
          console.log('Missing temporary_user data for registration:', registration.id, 'temporary_user_id:', registration.temporary_user_id);
        }
        
        return {
          ...registration,
          event: registration.event || { name: '', event_date: '' },
          category: registration.category || { name: '', vehicle_type: '', registration_fee: 0 },
          user: registration.user || null,
          temporary_user: registration.temporary_user || null,
          driver_profile: registration.driver_profile || null,
          allows_re_entry: false, // Will be populated in a separate optimized query
          re_entry_fee: 0 // Will be populated in a separate optimized query
        };
      });

      // Optimized query to fetch re-entry information for all registrations at once
      const globalCategoryIds = [...new Set(registrationsWithFees
        .map(reg => reg.category?.global_category_id)
        .filter(id => id))];
      
      let reEntryInfo: { [key: string]: { allows_re_entry: boolean; re_entry_fee: number } } = {};
      
      if (globalCategoryIds.length > 0) {
        const { data: globalCategories } = await supabase
          .from('global_categories')
          .select('id, re_entry_fee, allows_re_entry')
          .in('id', globalCategoryIds);
        
        if (globalCategories) {
          globalCategories.forEach(category => {
            reEntryInfo[category.id] = {
              allows_re_entry: category.allows_re_entry || false,
              re_entry_fee: category.re_entry_fee || 0
            };
          });
        }
      }
      
      // Update registrations with re-entry information
      const registrationsWithReEntryInfo = registrationsWithFees.map(registration => ({
        ...registration,
        allows_re_entry: registration.category?.global_category_id 
          ? reEntryInfo[registration.category.global_category_id]?.allows_re_entry || false
          : false,
        re_entry_fee: registration.category?.global_category_id
          ? reEntryInfo[registration.category.global_category_id]?.re_entry_fee || 0
          : 0
      }));
      
      setRegistrations(registrationsWithReEntryInfo);
      setTotalCount(registrationsWithReEntryInfo.length);
    } catch (error) {
      console.error('Error fetching registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering (separate from fetch state)
  useEffect(() => {
    let filtered = registrations;

    // Apply search filter
    if (debouncedSearchTerm.trim()) {
      const searchLower = debouncedSearchTerm.trim().toLowerCase();
      filtered = filtered.filter(registration => {
        return (
          // Direct registration fields
          (registration.full_name || '').toLowerCase().includes(searchLower) ||
          (registration.status || '').toLowerCase().includes(searchLower) ||
          (registration.payment_status || '').toLowerCase().includes(searchLower) ||
          (registration.id || '').toLowerCase().includes(searchLower) ||
          
          // User fields
          (registration.user?.full_name || '').toLowerCase().includes(searchLower) ||
          (registration.user?.email || '').toLowerCase().includes(searchLower) ||
          (registration.user?.phone || '').toLowerCase().includes(searchLower) ||
          
          // Temporary user fields
          (registration.temporary_user?.name || '').toLowerCase().includes(searchLower) ||
          (registration.temporary_user?.race_number || '').toLowerCase().includes(searchLower) ||
          
          // Event and category fields
          (registration.event?.name || '').toLowerCase().includes(searchLower) ||
          (registration.category?.name || '').toLowerCase().includes(searchLower) ||
          (registration.category?.vehicle_type || '').toLowerCase().includes(searchLower) ||
          
          // Driver profile fields (vehicle info)
          (registration.driver_profile?.vehicle_info?.raceNumber || '').toLowerCase().includes(searchLower) ||
          (registration.driver_profile?.vehicle_info?.vehicleBrandModel || '').toLowerCase().includes(searchLower) ||
          (registration.driver_profile?.vehicle_info?.year || '').toLowerCase().includes(searchLower) ||
          (registration.driver_profile?.vehicle_info?.make || '').toLowerCase().includes(searchLower) ||
          (registration.driver_profile?.vehicle_info?.model || '').toLowerCase().includes(searchLower) ||
          (registration.driver_profile?.vehicle_info?.color || '').toLowerCase().includes(searchLower) ||
          
          // Personal info fields
          (registration.driver_profile?.personal_info?.firstName || '').toLowerCase().includes(searchLower) ||
          (registration.driver_profile?.personal_info?.lastName || '').toLowerCase().includes(searchLower) ||
          (registration.driver_profile?.personal_info?.phone || '').toLowerCase().includes(searchLower) ||
          (registration.driver_profile?.personal_info?.email || '').toLowerCase().includes(searchLower) ||
          
          // Amount and tool car info
          (registration.amount_paid?.toString() || '').includes(searchLower) ||
          (registration.has_tool_car ? 'tool car' : '').toLowerCase().includes(searchLower) ||
          (registration.tool_car_count?.toString() || '').includes(searchLower)
        );
      });
    }

    // Apply past events filter
    if (!showPastEvents) {
      filtered = filtered.filter(registration => {
        if (!registration.event?.event_date) return false;
        
        // Use the same date parsing logic as isEventInPast
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day
        
        const [year, month, day] = registration.event.event_date.split('-');
        const event = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        
        return event >= today; // Show today and future events
      });
    }

    setFilteredRegistrations(filtered);
  }, [registrations, debouncedSearchTerm, showPastEvents]);

  // Fetch registrations when filters change (not search term)
  useEffect(() => {
    fetchRegistrations();
  }, [selectedStatus, selectedPaymentStatus, selectedEvent, showToolCarOnly]);

  // Calculate pagination for display
  const totalPages = Math.ceil(filteredRegistrations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRegistrations = filteredRegistrations.slice(startIndex, endIndex);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    fetchRevenueData();
    fetchEvents();
    fetchAvailableEvents();
  }, [user]);

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('id, name, event_date')
        .order('event_date', { ascending: false });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchAvailableEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('id, name, event_date')
        .eq('is_active', true)
        .order('event_date', { ascending: false });

      if (error) throw error;
      setAvailableEvents(data || []);
    } catch (error) {
      console.error('Error fetching available events:', error);
    }
  };

  const fetchEventCategories = async (eventId: string) => {
    try {
      const { data, error } = await supabase
        .from('event_categories')
        .select('id, name, vehicle_type, registration_fee')
        .eq('event_id', eventId)
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;
      setAvailableCategories(data || []);
    } catch (error) {
      console.error('Error fetching event categories:', error);
    }
  };

  const handleEventChange = (eventId: string) => {
    const event = availableEvents.find(e => e.id === eventId);
    setQuickRegEvent(event);
    if (eventId) {
      fetchEventCategories(eventId);
    } else {
      setAvailableCategories([]);
    }
  };

  const handleSearchDriver = async () => {
    if (!quickRegName.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setIsSearching(true);
    try {
      const searchTerm = quickRegName.trim();
      const results: any[] = [];

      // Search in driver_full_profile by name using the database function
      const { data: nameResults, error: nameError } = await supabase
        .rpc('search_driver_by_name', { search_term: searchTerm });

      if (nameError) throw nameError;
      if (nameResults) {
        results.push(...nameResults.map((profile: any) => ({
          id: `search-${profile.race_number}`, // Temporary ID for search results
          full_name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
          email: profile.email,
          phone: profile.phone,
          type: 'user',
          display_name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
          race_number: profile.race_number
        })));
      }

      // Search in driver_full_profile by race number
      const { data: raceResults, error: raceError } = await supabase
        .from('driver_full_profile')
        .select('*')
        .eq('race_number', searchTerm)
        .limit(5);

      if (raceError) throw raceError;
      if (raceResults) {
        results.push(...raceResults.map((profile: any) => ({
          id: `search-${profile.race_number}`, // Temporary ID for search results
          full_name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
          email: profile.email,
          phone: profile.phone,
          type: 'user',
          display_name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
          race_number: profile.race_number
        })));
      }

      // Don't search in temporary_user table for quick registration
      // Temporary users should only be created when manually entering name/race number

      // Remove duplicates based on id and type
      const uniqueResults = results.filter((result, index, self) => 
        index === self.findIndex(r => r.id === result.id && r.type === result.type)
      );

      setSearchResults(uniqueResults);
    } catch (error) {
      console.error('Error searching drivers:', error);
      toast.error('Failed to search drivers');
    } finally {
      setIsSearching(false);
    }
  };

  const handleRaceNumberCheck = async () => {
    if (!quickRegRaceNumber.trim()) {
      toast.error('Please enter a race number');
      return;
    }

    try {
      console.log('Checking race number:', quickRegRaceNumber);
      
      // Check in driver_full_profile
      const { data: profileData, error: profileError } = await supabase
        .from('driver_full_profile')
        .select('first_name, last_name')
        .eq('race_number', quickRegRaceNumber);

      if (profileError) {
        console.error('Error checking driver_full_profile:', profileError);
        throw profileError;
      }

      console.log('Profile data found:', profileData);

      if (profileData && profileData.length > 0) {
        setRaceNumberExists(true);
        const profile = profileData[0];
        const driverName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
        setExistingDriverName(driverName);
        console.log('Race number exists in driver_full_profile:', driverName);
        return;
      }

      // Check in temporary_user
      const { data: tempData, error: tempError } = await supabase
        .from('temporary_user')
        .select('name')
        .eq('race_number', quickRegRaceNumber);

      if (tempError) {
        console.error('Error checking temporary_user:', tempError);
        throw tempError;
      }

      console.log('Temporary user data found:', tempData);

      if (tempData && tempData.length > 0) {
        setRaceNumberExists(true);
        setExistingDriverName(tempData[0].name);
        console.log('Race number exists in temporary_user:', tempData[0].name);
        return;
      }

      setRaceNumberExists(false);
      setExistingDriverName('');
      console.log('Race number is available');
    } catch (error) {
      console.error('Error checking race number:', error);
      toast.error('Failed to check race number');
    }
  };

  const handleSelectSearchResult = (result: any) => {
    setQuickRegUser(result);
    setQuickRegName(result.display_name || result.full_name);
    if (result.race_number) {
      setQuickRegRaceNumber(result.race_number);
    }
    setSearchResults([]);
  };

  const handleQuickRegistration = async () => {
    if (!quickRegEvent || !quickRegCategory) {
      toast.error('Please select an event and category');
      return;
    }

    if (!quickRegUser && (!quickRegName || !quickRegRaceNumber)) {
      toast.error('Please either select a user or enter name and race number');
      return;
    }

    setIsCreatingRegistration(true);
    try {
      let registrationData: any = {
        event_id: quickRegEvent.id,
        category_id: quickRegCategory.id,
        amount_paid: quickRegCategory.registration_fee,
        status: 'completed',
        payment_status: 'paid',
        has_tool_car: false,
        tool_car_count: 0
      };

      if (quickRegUser) {
        // User found from search - create temporary user for them
        const { data: tempUser, error: tempError } = await supabase
          .from('temporary_user')
          .insert({
            name: quickRegUser.full_name || quickRegUser.display_name,
            race_number: quickRegUser.race_number
          })
          .select()
          .single();

        if (tempError) {
          console.error('Error creating temporary user for found driver:', tempError);
          
          // Check if it's a duplicate race number error
          if (tempError.code === '23505' || tempError.message.includes('duplicate')) {
            toast.error(`Race number ${quickRegUser.race_number} already exists. Please use a different race number.`);
          } else {
            toast.error(`Failed to create temporary user: ${tempError.message}`);
          }
          throw tempError;
        }
        
        registrationData.temporary_user_id = tempUser.id;
      } else {
        // Create temporary user and register
        
        const { data: tempUser, error: tempError } = await supabase
          .from('temporary_user')
          .insert({
            name: quickRegName.trim(),
            race_number: quickRegRaceNumber.trim()
          })
          .select()
          .single();

        if (tempError) {
          console.error('Error creating temporary user:', tempError);
          
          // Check if it's a duplicate race number error
          if (tempError.code === '23505' || tempError.message.includes('duplicate')) {
            toast.error(`Race number ${quickRegRaceNumber} already exists. Please use a different race number.`);
          } else {
            toast.error(`Failed to create temporary user: ${tempError.message}`);
          }
          throw tempError;
        }
        registrationData.temporary_user_id = tempUser.id;
      }

      
      const { data, error } = await supabase
        .from('event_registrations')
        .insert(registrationData)
        .select()
        .single();

      if (error) {
        console.error('Error creating registration:', error);
        toast.error(`Failed to create registration: ${error.message}`);
        throw error;
      }

      console.log('Registration created successfully:', data);
      toast.success('Registration created successfully!');
      resetQuickRegForm();
      fetchRegistrations();
    } catch (error: any) {
      console.error('Error creating registration:', error);
      // Don't show generic error message if we already showed a specific one
      if (!error?.message || !error.message.includes('Failed to create')) {
        toast.error('Failed to create registration');
      }
    } finally {
      setIsCreatingRegistration(false);
    }
  };

  const resetQuickRegForm = () => {
    setQuickRegEvent(null);
    setQuickRegCategory(null);
    setQuickRegUser(null);
    setQuickRegName('');
    setQuickRegRaceNumber('');
    setSearchResults([]);
    setRaceNumberExists(false);
    setExistingDriverName('');
  };

  const getFilteredEvents = () => {
    if (showPastEvents) return events;
      return events.filter(event => !isEventInPast(event.event_date));
  };

  const completeRegistration = async (registrationId: string) => {
    try {
      const { error } = await supabase
        .from('event_registrations')
        .update({ status: 'completed' })
        .eq('id', registrationId);

      if (error) throw error;
      toast.success('Registration completed successfully!');
      fetchRegistrations();
    } catch (error) {
      console.error('Error completing registration:', error);
      toast.error('Failed to complete registration');
    }
  };

  const updateRegistrationStatus = async (registrationId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('event_registrations')
        .update({ status })
        .eq('id', registrationId);

      if (error) throw error;
      toast.success(`Registration status updated to ${status}!`);
      fetchRegistrations();
    } catch (error) {
      console.error('Error updating registration status:', error);
      toast.error('Failed to update registration status');
    }
  };

  const updatePaymentStatus = async (registrationId: string, paymentStatus: string) => {
    try {
      const { error } = await supabase
        .from('event_registrations')
        .update({ payment_status: paymentStatus })
        .eq('id', registrationId);

      if (error) throw error;
      toast.success(`Payment status updated to ${paymentStatus}!`);
      fetchRegistrations();
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('Failed to update payment status');
    }
  };

  const resendConfirmationEmail = async (registrationId: string) => {
    try {
      const { data: registration, error: fetchError } = await supabase
        .from('event_registrations')
        .select(`
          *,
          event:events!event_registrations_event_id_fkey(name, event_date),
          category:event_categories(name),
          user:users(full_name, email),
          temporary_user:temporary_user(name, race_number)
        `)
        .eq('id', registrationId)
        .single();

      if (fetchError) throw fetchError;

      if (!registration) {
        toast.error('Registration not found');
        return;
      }

      // Check if this is a real user registration (not temporary user)
      if (!registration.user_id || !registration.user?.email) {
        toast.error('Cannot send email to temporary user registration');
        return;
      }

      // Import the email service singleton
      const { emailService } = await import('../../lib/emailService');
      
      // Prepare registration data for email
      const registrationData = {
        driver_name: registration.user.full_name,
        event_name: registration.event.name,
        event_date: formatDateForDisplay(registration.event.event_date),
        registration_deadline: '', // Not available in current data
        categories: [{
          name: registration.category.name,
          vehicle_type: registration.category.vehicle_type || 'Unknown',
          registration_fee: registration.amount_paid,
          wristbands_included: 0,
          tickets_included: 0,
          first_place_prize: 0,
          second_place_prize: 0
        }],
        total_amount: registration.amount_paid,
        has_tool_car: registration.has_tool_car || false,
        tool_car_count: registration.tool_car_count || 0,
        tickets_enabled: false,
        event_id: registration.event_id
      };

        await emailService.sendEventRegistrationConfirmation(
          registration.user_id,
          registration.user.email,
        registrationData
        );

      toast.success('Confirmation email sent successfully!');
    } catch (error) {
      console.error('Error sending confirmation email:', error);
      toast.error('Failed to send confirmation email');
    }
  };

  const getVehicleIcon = (vehicleType: string) => {
    switch (vehicleType) {
      case 'car': return <Car className="h-4 w-4" />;
      case 'motorcycle': return <Bike className="h-4 w-4" />;
      case 'truck': return <Truck className="h-4 w-4" />;
      default: return <Car className="h-4 w-4" />;
    }
  };
  
  const fetchRevenueData = async () => {
    try {
      const { data, error } = await supabase
        .from('event_registrations')
        .select(`
          *,
          event:events!event_registrations_event_id_fkey(name, event_date),
          category:event_categories(name, registration_fee)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRevenueData(data || []);
    } catch (error) {
      console.error('Error fetching revenue data:', error);
    }
  };

  const calculateTotalRevenue = () => {
    return revenueData.reduce((total: number, registration: any) => {
      return total + (registration.amount_paid || 0);
    }, 0);
  };

  const getFilteredRevenueData = () => {
    let filtered = revenueData;

    if (selectedEvent && selectedEvent !== 'all') {
      filtered = filtered.filter((r: any) => r.event_id === selectedEvent);
    }

    if (!showPastEvents) {
      filtered = filtered.filter((r: any) => {
        if (!r.event?.event_date) return false;
        
        // Use the same date parsing logic as isEventInPast
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day
        
        const [year, month, day] = r.event.event_date.split('-');
        const event = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        
        return event >= today; // Show today and future events
      });
    }

    return filtered;
  };

  // Calculate metrics based on current filtered registrations
  const getFilteredMetrics = () => {
    const filtered = filteredRegistrations;
    
    const totalRegistrations = filtered.length;
    const readyToCompete = filtered.filter(r => r.status === 'completed').length;
    const pendingPayment = filtered.filter(r => r.status === 'pending').length;
    const totalRevenue = filtered.filter(r => r.status === 'completed').reduce((sum, r) => sum + (r.amount_paid || 0), 0);
    
    return {
      totalRegistrations,
      readyToCompete,
      pendingPayment,
      totalRevenue
    };
  };

  // Memoize metrics to prevent unnecessary recalculations
  const metrics = getFilteredMetrics();

  // Export registrations to Excel
  const handleExportToExcel = async () => {
    try {
      // Use the currently filtered registrations instead of fetching from database
      const registrationsToExport = filteredRegistrations;

      if (!registrationsToExport || registrationsToExport.length === 0) {
        toast.error('No registrations found to export');
        return;
      }

      // Define headers for Excel
      const headers = [
        'Driver Name',
        'Email',
        'Phone',
        'Event',
        'Event Date',
        'Category',
        'Vehicle Type',
        'Race Number',
        'Vehicle',
        'Year',
        'Tool Car',
        'Tool Car Count',
        'Status',
        'Payment Status',
        'Registration Fee'
      ];

      // Map function to convert each registration to a row
      const mapRegistrationToRow = (reg: any) => [
        reg.full_name || reg.user?.full_name || reg.temporary_user?.name || '',
        reg.user?.email || '',
        reg.user?.phone || '',
        reg.event?.name || '',
        formatDateForDisplay(reg.event?.event_date) || '',
        reg.category?.name || '',
        reg.category?.vehicle_type || '',
        reg.driver_profile?.vehicle_info?.raceNumber || reg.temporary_user?.race_number || '',
        reg.driver_profile?.vehicle_info?.vehicleBrandModel || '',
        reg.driver_profile?.vehicle_info?.year || '',
        reg.has_tool_car ? 'Yes' : 'No',
        reg.has_tool_car ? (reg.tool_car_count || 1) : 0,
        reg.status || '',
        reg.payment_status || '',
        reg.amount_paid || 0
      ];

      // Get event name for title based on current filters
      let eventTitle = 'Event Registrations';
      if (selectedEvent && selectedEvent !== 'all') {
        const eventData = events.find(e => e.id === selectedEvent);
        if (eventData) {
          eventTitle = `${eventData.name} - Registrations`;
        }
      } else if (debouncedSearchTerm.trim()) {
        eventTitle = `Search Results - "${debouncedSearchTerm.trim()}"`;
      }

      // Use the utility to export
      exportToExcel(
        registrationsToExport,
        headers,
        'event-registrations',
        mapRegistrationToRow,
        eventTitle
      );
      
      toast.success(`Exported ${registrationsToExport.length} registrations to Excel`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export registrations to Excel');
    }
  };

  // Handler for admin re-entry registration
  const handleAdminRegisterReEntry = (registration: EventRegistration) => {
    setAdminReEntryRegistration(registration);
    setShowAdminReEntryModal(true);
  };

  const handleConfirmAdminReEntry = async () => {
    if (!adminReEntryRegistration) return;
    setIsRegisteringReEntry(true);
    try {
      const { registerReEntry } = await import('../../lib/reEntryUtils');
      const userId = adminReEntryRegistration.user_id || adminReEntryRegistration.temporary_user_id;
      if (!userId) {
        toast.error('No user ID found for re-entry registration');
        return;
      }
      await registerReEntry(
        userId,
        adminReEntryRegistration.event_id,
        adminReEntryRegistration.category_id
      );
      toast.success('Re-entry registration successful!');
      setShowAdminReEntryModal(false);
      setAdminReEntryRegistration(null);
      fetchRegistrations();
    } catch (error) {
      toast.error('Failed to register re-entry');
    } finally {
      setIsRegisteringReEntry(false);
    }
  };

  if (loading && currentPage === 1) { // Only show full loading screen on initial load
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-black py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-white mb-8">Event Registration Management</h1>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-zinc-800">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('registrations')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'registrations'
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-gray-400 hover:text-white hover:border-gray-300'
                }`}
              >
                <User className="h-5 w-5 inline mr-2" />
                Registrations
              </button>
              <button
                onClick={() => setActiveTab('re-entry')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 're-entry'
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-gray-400 hover:text-white hover:border-gray-300'
                }`}
              >
                <RotateCcw className="h-5 w-5 inline mr-2" />
                Re-entry Management
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'registrations' && (
          <>
            {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-zinc-900 p-6 rounded-lg">
            <div className="flex items-center gap-4">
              <Trophy className="h-8 w-8 text-primary-500 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-white">Total Registrations</h3>
                <p className="text-2xl font-bold text-primary-500">{metrics.totalRegistrations}</p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900 p-6 rounded-lg">
            <div className="flex items-center gap-4">
              <CheckCircle className="h-8 w-8 text-green-500 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-white">Ready to Compete</h3>
                <p className="text-2xl font-bold text-green-500">
                  {metrics.readyToCompete}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900 p-6 rounded-lg">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8 text-yellow-500 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-white">Pending Payment</h3>
                <p className="text-2xl font-bold text-yellow-500">
                  {metrics.pendingPayment}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900 p-6 rounded-lg">
            <div className="flex items-center gap-4">
              <DollarSign className="h-8 w-8 text-primary-500 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-white">Total Revenue</h3>
                <p className="text-2xl font-bold text-primary-500">
                  {formatCurrency(metrics.totalRevenue)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 space-y-4 relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search registrations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-zinc-900 text-white pl-10 pr-4 py-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
              />
            </div>

            <select
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className="bg-zinc-900 text-white px-4 py-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
            >
              <option value="all">All Events</option>
              {getFilteredEvents().map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name} - {formatDateForDisplay(event.event_date)}
                  {isEventInPast(event.event_date) ? ' (Past)' : ''}
                </option>
              ))}
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="bg-zinc-900 text-white px-4 py-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Ready to Compete</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <select
              value={selectedPaymentStatus}
              onChange={(e) => setSelectedPaymentStatus(e.target.value)}
              className="bg-zinc-900 text-white px-4 py-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
            >
              <option value="all">All Payment Status</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="refunded">Refunded</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <div className="flex gap-2 items-center">
              <button 
                onClick={() => setShowPastEvents(!showPastEvents)}
                className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white px-3 py-2 rounded-lg"
                title={showPastEvents ? "Hide past events" : "Show past events"}
              >
                <Filter className="h-4 w-4" />
                {showPastEvents ? "Hide Past" : "Show Past"}
              </button>
            </div>

            <div className="flex gap-2">
              <button 
                onClick={handleExportToExcel}
                className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed min-w-fit"
                title={`Export ${filteredRegistrations.length} filtered registrations to Excel`}
                disabled={filteredRegistrations.length === 0}
              >
                <Download className="h-4 w-4" />
                <span className="whitespace-nowrap">Export to Excel ({filteredRegistrations.length})</span>
              </button>
              
              <button 
                onClick={() => setShowQuickRegModal(true)}
                className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg whitespace-nowrap min-w-fit"
                title="Quick register a new driver"
              >
                <UserPlus className="h-4 w-4" />
                <span className="whitespace-nowrap">Quick Register</span>
              </button>
            </div>
          </div>
        </div>

        {/* Registrations Table */}
        <div className="bg-zinc-900 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-zinc-800">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Driver
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Event & Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Vehicle Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Registration Fee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800 text-sm">
                {currentRegistrations.map((registration) => (
                  <tr key={registration.id} className="hover:bg-zinc-800">
                    {/* DRIVER COLUMN */}
                    <td className="px-6 py-3 whitespace-nowrap align-top">
                      <div className="flex flex-col">
                        <span className="font-semibold text-white">
                          {registration.full_name || registration.user?.full_name || registration.temporary_user?.name || 'No Name'}
                        </span>
                        {registration.user?.email && (
                          <span className="text-xs text-gray-400">{registration.user.email}</span>
                        )}
                        {registration.user?.phone && (
                          <span className="text-xs text-gray-400">{registration.user.phone}</span>
                        )}
                        {registration.temporary_user && (
                          <span className="text-xs text-orange-400">Temporary User</span>
                        )}
                      </div>
                    </td>
                    {/* EVENT & CATEGORY COLUMN */}
                    <td className="px-6 py-3 whitespace-nowrap align-top">
                      <div className="flex flex-col gap-1">
                        <span className="font-semibold text-white">
                          {registration.event?.name || ''}
                        </span>
                        <span className="text-xs text-gray-300 flex items-center gap-2">
                          {registration.event?.event_date && (
                            <>
                          {formatDateForDisplay(registration.event.event_date)}
                          {isEventInPast(registration.event.event_date) && (
                                <span className="ml-1 px-2 py-0.5 rounded bg-zinc-700 text-xs text-gray-200">Past</span>
                              )}
                            </>
                          )}
                        </span>
                        <span className="flex items-center gap-2 mt-1">
                          <span className="text-red-400 flex items-center gap-1 font-medium">
                            {getVehicleIcon(registration.category?.vehicle_type || 'car')}
                            {registration.category?.name || ''}
                          </span>
                        {registration.original_registration_id && (
                            <span className="ml-1 px-2 py-0.5 rounded bg-red-900 text-xs text-red-200 font-semibold">Re-Enter</span>
                          )}
                        </span>
                      </div>
                    </td>
                    {/* VEHICLE INFO COLUMN */}
                    <td className="px-6 py-3 whitespace-nowrap align-top">
                      <div className="flex flex-col">
                        <span className="text-white font-medium">
                          Race #: {registration.driver_profile?.vehicle_info?.raceNumber || registration.temporary_user?.race_number || 'N/A'}
                        </span>
                        {registration.driver_profile?.vehicle_info?.year && (
                          <span className="text-xs text-gray-400 mt-1">{registration.driver_profile.vehicle_info.year}</span>
                        )}
                        {registration.temporary_user && !registration.driver_profile && (
                          <span className="text-xs text-orange-400">No vehicle details</span>
                        )}
                      </div>
                    </td>
                    {/* REGISTRATION FEE COLUMN */}
                    <td className="px-6 py-3 whitespace-nowrap align-top">
                      <span className="font-bold text-white">
                        xcg {Number(registration.amount_paid).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        registration.status === 'completed' ? 'bg-green-100 text-green-800' : 
                        registration.status === 'confirmed' ? 'bg-blue-100 text-blue-800' : 
                        registration.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-red-100 text-red-800'
                      }`}>
                        {registration.status === 'completed' ? 'Ready to Compete' : registration.status}
                      </span>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      {registration.status !== 'completed' && registration.status !== 'cancelled' ? (
                        <div className="flex flex-col gap-2">
                          {/* Primary Action: Complete Registration (Paid + Ready to Compete) */}
                          {registration.status !== 'completed' && registration.payment_status !== 'paid' && (
                            <button
                              onClick={() => completeRegistration(registration.id)}
                              className="text-white hover:text-green-100 text-sm px-3 py-2 bg-green-600 hover:bg-green-700 rounded font-semibold transition-colors"
                              title="Mark as Paid & Ready to Compete"
                            >
                              Complete Registration
                            </button>
                          )}

                          {/* Secondary Actions */}
                          <div className="flex gap-1">
                          

                            {registration.status !== 'cancelled' && (
                              <button
                                onClick={() => updateRegistrationStatus(registration.id, 'cancelled')}
                                className="text-white hover:text-primary-100 text-xs px-2 py-1 bg-primary-600 hover:bg-primary-700 rounded transition-colors"
                                title="Cancel Registration"
                              >
                                Cancel
                              </button>
                            )}
                            {registration.payment_status === 'paid' && (
                              <button
                                onClick={() => updatePaymentStatus(registration.id, 'refunded')}
                                className="text-white hover:text-blue-100 text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                                title="Process Refund"
                              >
                                Refund
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400">
                          {registration.status === 'completed' ? 'Ready to compete' : 'Cancelled'}
                        </div>
                      )}
                      {/* Register Re-entry button for eligible registrations */}
                      {(!registration.original_registration_id && registration.allows_re_entry && registration.status === 'completed' && (registration.re_entry_count === undefined || registration.re_entry_count < 1)) && (
                        <button
                          onClick={() => handleAdminRegisterReEntry(registration)}
                          className="mt-2 text-xs bg-orange-600 hover:bg-orange-700 text-white px-2 py-1 rounded"
                        >
                          Register Re-entry
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={totalCount}
          />
        </div>

        {currentRegistrations.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg">No registrations found</div>
          </div>
        )}
          </>
        )}

        {/* Re-entry Management Tab */}
        {activeTab === 're-entry' && (
          <div>
            <ReEntryManagement eventId={selectedEvent === 'all' ? '' : selectedEvent} />
          </div>
        )}

        {/* Quick Registration Modal */}
        {showQuickRegModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-white">Quick Driver Registration</h2>
                <button onClick={() => setShowQuickRegModal(false)}>
                  <X className="h-6 w-6 text-gray-400 hover:text-white" />
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Event Selection */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Select Event *
                  </label>
                  <select
                    value={quickRegEvent?.id || ''}
                    onChange={(e) => handleEventChange(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white"
                    required
                  >
                    <option value="">Select an event</option>
                    {availableEvents.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.name} - {formatDateForDisplay(event.event_date)}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Category Selection */}
                {quickRegEvent && (
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Select Category *
                    </label>
                    <select
                      value={quickRegCategory?.id || ''}
                      onChange={(e) => {
                        const category = availableCategories.find(c => c.id === e.target.value);
                        setQuickRegCategory(category);
                      }}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white"
                      required
                    >
                      <option value="">Select a category</option>
                      {availableCategories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name} ({category.vehicle_type}) - {formatCurrency(category.registration_fee)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Driver Name */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Driver Name or Race Number *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={quickRegName}
                      onChange={(e) => setQuickRegName(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white"
                      placeholder="Enter driver name or race number"
                      required
                    />
                    <button
                      type="button"
                      onClick={handleSearchDriver}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      <Search className="h-5 w-5" />
                    </button>
                    
                    {/* Search Results Dropdown */}
                    {searchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-md shadow-lg max-h-60 overflow-auto">
                        {searchResults.map((result, index) => (
                          <div 
                            key={`${result.type}-${result.id}-${index}`}
                            className="p-3 hover:bg-zinc-700 cursor-pointer border-b border-zinc-700 last:border-b-0"
                            onClick={() => handleSelectSearchResult(result)}
                          >
                            <div className="font-medium text-white">
                              {result.display_name || result.full_name}
                              {result.type === 'temporary' && (
                                <span className="ml-2 text-xs bg-orange-600 text-white px-2 py-1 rounded">
                                  Temporary
                                </span>
                              )}
                            </div>
                            {result.race_number && (
                              <div className="text-sm text-gray-400">
                                Race #: {result.race_number}
                              </div>
                            )}
                            {result.email && (
                              <div className="text-sm text-gray-400">
                                {result.email}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Race Number */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Race Number *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={quickRegRaceNumber}
                      onChange={(e) => setQuickRegRaceNumber(e.target.value)}
                      onBlur={handleRaceNumberCheck}
                      className={`flex-1 bg-zinc-800 border ${raceNumberExists ? 'border-primary-500' : 'border-zinc-700'} rounded-md p-3 text-white`}
                      placeholder="Enter race number"
                      required
                    />
                  </div>
                  {raceNumberExists && (
                    <p className="text-yellow-500 text-sm mt-1">
                      ⚠️ Race number already used by {existingDriverName}. You can still register with this race number.
                    </p>
                  )}
                </div>
                
                {/* Registration Fee Display */}
                {quickRegCategory && (
                  <div className="p-4 bg-zinc-800 rounded-lg mt-4 border border-zinc-700">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Registration Fee:</span>
                      <span className="text-xl font-bold text-primary-500">
                        {formatCurrency(quickRegCategory.registration_fee)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Payment will be marked as paid automatically
                    </p>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowQuickRegModal(false)}
                    className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-3 rounded-lg font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleQuickRegistration}
                    disabled={isCreatingRegistration || !quickRegEvent || !quickRegCategory || (!quickRegUser && (!quickRegName || !quickRegRaceNumber))}
                    className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
                  >
                    {isCreatingRegistration ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Registering...
                      </span>
                    ) : 'Register Driver'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Admin Re-entry Modal */}
        {showAdminReEntryModal && adminReEntryRegistration && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-white">Register Re-entry</h3>
                <button onClick={() => setShowAdminReEntryModal(false)}>
                  <X className="h-5 w-5 text-gray-400 hover:text-white" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="font-medium text-white text-lg">
                    {adminReEntryRegistration.user?.full_name || adminReEntryRegistration.temporary_user?.name}
                  </div>
                  <div className="text-sm text-gray-400">
                    Race #: {adminReEntryRegistration.driver_profile?.vehicle_info?.raceNumber || adminReEntryRegistration.temporary_user?.race_number || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-400">
                    Event: {adminReEntryRegistration.event.name}
                  </div>
                  <div className="text-sm text-gray-400">
                    Category: {adminReEntryRegistration.category.name}
                  </div>
                </div>
                
                {/* Re-entry Fee Display */}
                {adminReEntryRegistration.re_entry_fee && adminReEntryRegistration.re_entry_fee > 0 && (
                  <div className="p-4 bg-zinc-800 rounded-lg border border-zinc-700">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Re-entry Fee:</span>
                      <span className="text-xl font-bold text-primary-500">
                        {formatCurrency(adminReEntryRegistration.re_entry_fee)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      This fee will be charged for the re-entry registration
                    </p>
                  </div>
                )}
                
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setShowAdminReEntryModal(false)}
                    className="px-4 py-2 text-gray-300 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmAdminReEntry}
                    disabled={isRegisteringReEntry}
                    className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRegisteringReEntry ? 'Registering...' : 'Register Re-entry'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}